import { Utils } from "../utils/utils";
import DragDropResizableOptions from '../interfaces/DragDropResizableOptions';
import ItemHTMLElement from '../interfaces/ItemHTMLElement';
import HTMLElementExtendOptions from "../interfaces/HTMLElementExtendOptions";
import Rect from '../interfaces/Rect';
import RectScaleReciprocal from '../interfaces/RectScaleReciprocal';
import Size from '../interfaces/Size';
import { DragDropBase } from "./DragDropBase";
import DragDropResizableHandle from './DragDropResizableHandle';
import DragDropManager from './DragDropManager';
import DragDropUIData from '../interfaces/DragDropUIData';

/** 
 * @internal 
 * Class for a drag drop resizable
 */
export default class DragDropResizable extends DragDropBase implements HTMLElementExtendOptions<DragDropResizableOptions>
{
  /** @internal */
  protected handlers: DragDropResizableHandle[];
  /** @internal */
  protected originalRect: Rect;
  /** @internal */
  protected rectScale: RectScaleReciprocal = { x: 1, y: 1 };
  /** @internal */
  protected temporalRect: Rect;
  /** @internal */
  protected scrollY: number;
  /** @internal */
  protected scrolled: number;
  /** @internal */
  protected scrollEl: HTMLElement;
  /** @internal */
  protected startEvent: MouseEvent;
  /** @internal value saved in the same order as _originStyleProp[] */
  protected elOriginStyleVal: string[];
  /** @internal */
  protected parentOriginStylePosition: string;
  /** @internal */
  protected static _originStyleProp = [ 'width', 'height', 'position', 'left', 'top', 'opacity', 'zIndex' ];
  /** @internal */
  protected sizeToContent: boolean;

  /**
   * Constructor
   * Note: have to be public else complains for HTMLElementExtendOpt ?
   * @param el 
   * @param option 
   */
  constructor (public el: ItemHTMLElement, public option: DragDropResizableOptions = {})
  {
    super();
    // create var event binding so we can easily remove and still look like TS methods (unlike anonymous functions)
    this._mouseOver = this._mouseOver.bind(this);
    this._mouseOut = this._mouseOut.bind(this);
    this.enable();
    this._setupAutoHide(this.option.autoHide);
    this._setupHandlers();
  }

  /**
   * Add an event listener
   * @param event The event to listen for
   * @param callback The callback to call when the event is triggered
   */
  public on (event: 'resizestart' | 'resize' | 'resizestop', callback: (event: DragEvent) => void): void
  {
    super.on(event, callback);
  }

  /**
   * Remove an event listener
   * @param event The event to remove
   */
  public off (event: 'resizestart' | 'resize' | 'resizestop'): void
  {
    super.off(event);
  }

  /**
   * Enable the resizable
   */
  public enable (): void
  {
    super.enable();
    this.el.classList.remove('ui-resizable-disabled');
    this._setupAutoHide(this.option.autoHide);
  }

  /**
   * Disable the resizable
   */
  public disable (): void
  {
    super.disable();
    this.el.classList.add('ui-resizable-disabled');
    this._setupAutoHide(false);
  }

  /**
   * Destroy the resizable
   */
  public destroy (): void
  {
    this._removeHandlers();
    this._setupAutoHide(false);
    delete this.el;
    super.destroy();
  }

  /**
   * Update the options for the resizable
   * @param opts The new options
   * @returns {DragDropResizable}
   */
  public updateOption (opts: DragDropResizableOptions): DragDropResizable
  {
    let updateHandles = (opts.handles && opts.handles !== this.option.handles);
    let updateAutoHide = (opts.autoHide && opts.autoHide !== this.option.autoHide);
    Object.keys(opts).forEach(key => this.option[ key ] = opts[ key ]);
    if (updateHandles)
    {
      this._removeHandlers();
      this._setupHandlers();
    }

    if (updateAutoHide)
    {
      this._setupAutoHide(this.option.autoHide);
    }
    return this;
  }

  /** 
   * @internal turns auto hide on/off 
   * @param {boolean} auto True to turn on auto hide
   * @returns {DragDropResizable}
   */
  protected _setupAutoHide (auto: boolean): DragDropResizable
  {
    if (auto)
    {
      this.el.classList.add('ui-resizable-autohide');
      // use mouseover and not mouseenter to get better performance and track for nested cases
      this.el.addEventListener('mouseover', this._mouseOver);
      this.el.addEventListener('mouseout', this._mouseOut);
    } else
    {
      this.el.classList.remove('ui-resizable-autohide');
      this.el.removeEventListener('mouseover', this._mouseOver);
      this.el.removeEventListener('mouseout', this._mouseOut);
      if (DragDropManager.overResizeElement === this)
      {
        delete DragDropManager.overResizeElement;
      }
    }
    return this;
  }

  /** 
   * @internal 
   * Called when mouse enters the element
   * @param {Event} e The mouse event
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected _mouseOver (e: Event): void
  {
    // already over a child, ignore. Ideally we just call e.stopPropagation()
    if (DragDropManager.overResizeElement || DragDropManager.dragElement) 
      return;

    DragDropManager.overResizeElement = this;
    this.el.classList.remove('ui-resizable-autohide');
  }

  /** 
   * @internal 
   * Called when mouse leaves the element
   * @param {Event} e The mouse event
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected _mouseOut (e: Event): void
  {
    if (DragDropManager.overResizeElement !== this) 
      return;

    delete DragDropManager.overResizeElement;
    this.el.classList.add('ui-resizable-autohide');
  }

  /** 
   * @internal 
   * Setup the handlers for resizing
   * @returns {DragDropResizable}
   */
  protected _setupHandlers (): DragDropResizable
  {
    this.handlers = this.option.handles.split(',')
      .map(dir => dir.trim())
      .map(dir => new DragDropResizableHandle(this.el, dir, {
        start: (event: MouseEvent) =>
        {
          this._resizeStart(event);
        },
        stop: (event: MouseEvent) =>
        {
          this._resizeStop(event);
        },
        move: (event: MouseEvent) =>
        {
          this._resizing(event, dir);
        }
      }));
    return this;
  }

  /** 
   * @internal 
   * Called when resizing starts
   * @param {MouseEvent} event The mouse event
   * @returns {DragDropResizable}
   */
  protected _resizeStart (event: MouseEvent): DragDropResizable
  {
    this.sizeToContent = Utils.shouldSizeToContent(this.el.tileLayoutNode, true); // strick true only and not number
    this.originalRect = this.el.getBoundingClientRect();
    this.scrollEl = Utils.getScrollElement(this.el);
    this.scrollY = this.scrollEl.scrollTop;
    this.scrolled = 0;
    this.startEvent = event;
    this._setupHelper();
    this._applyChange();
    const ev = Utils.initEvent<MouseEvent>(event, { type: 'resizestart', target: this.el });
    if (this.option.start)
    {
      this.option.start(ev, this._ui());
    }
    this.el.classList.add('ui-resizable-resizing');
    this.triggerEvent('resizestart', ev);
    return this;
  }

  /** 
   * @internal 
   * Called when resizing is happening
   * @param {MouseEvent} event The mouse event
   * @param {string} dir The direction of the resize
   * @returns {DragDropResizable}
   */
  protected _resizing (event: MouseEvent, dir: string): DragDropResizable
  {
    this.scrolled = this.scrollEl.scrollTop - this.scrollY;
    this.temporalRect = this._getChange(event, dir);
    this._applyChange();
    const ev = Utils.initEvent<MouseEvent>(event, { type: 'resize', target: this.el });
    if (this.option.resize)
    {
      this.option.resize(ev, this._ui());
    }
    this.triggerEvent('resize', ev);
    return this;
  }

  /** 
   * @internal 
   * Called when resizing stops
   * @param {MouseEvent} event The mouse event
   * @returns {DragDropResizable}
   */
  protected _resizeStop (event: MouseEvent): DragDropResizable
  {
    const ev = Utils.initEvent<MouseEvent>(event, { type: 'resizestop', target: this.el });
    if (this.option.stop)
    {
      this.option.stop(ev); // Note: ui() not used by layout so don't pass
    }
    this.el.classList.remove('ui-resizable-resizing');
    this.triggerEvent('resizestop', ev);
    this._cleanHelper();
    delete this.startEvent;
    delete this.originalRect;
    delete this.temporalRect;
    delete this.scrollY;
    delete this.scrolled;
    return this;
  }

  /** 
   * @internal 
   * Setup the helper element
   * @returns {DragDropResizable}
   */
  protected _setupHelper (): DragDropResizable
  {
    this.elOriginStyleVal = DragDropResizable._originStyleProp.map(prop => this.el.style[ prop ]);
    this.parentOriginStylePosition = this.el.parentElement.style.position;

    const parent = this.el.parentElement;
    const dragTransform = Utils.getValuesFromTransformedElement(parent);
    this.rectScale = {
      x: dragTransform.xScale,
      y: dragTransform.yScale
    };

    if (getComputedStyle(this.el.parentElement).position.match(/static/))
    {
      this.el.parentElement.style.position = 'relative';
    }
    this.el.style.position = 'absolute';
    this.el.style.opacity = '0.8';
    return this;
  }

  /** 
   * @internal 
   * Clean up the helper element
   * @returns {DragDropResizable}
   */
  protected _cleanHelper (): DragDropResizable
  {
    DragDropResizable._originStyleProp.forEach((prop, i) =>
    {
      this.el.style[ prop ] = this.elOriginStyleVal[ i ] || null;
    });
    this.el.parentElement.style.position = this.parentOriginStylePosition || null;
    return this;
  }

  /** 
   * @internal 
   * Get the change in size based on the event and direction
   * @param {MouseEvent} event The mouse event
   * @param {string} dir The direction of the resize
   * @returns {Rect} The new rect
   */
  protected _getChange (event: MouseEvent, dir: string): Rect
  {
    const oEvent = this.startEvent;
    const newRect = {
      width: this.originalRect.width,
      height: this.originalRect.height + this.scrolled,
      left: this.originalRect.left,
      top: this.originalRect.top - this.scrolled
    };

    const offsetX = event.clientX - oEvent.clientX;
    const offsetY = this.sizeToContent ? 0 : event.clientY - oEvent.clientY; // prevent vert resize
    let moveLeft: boolean;
    let moveUp: boolean;

    if (dir.indexOf('e') > -1)
    {
      newRect.width += offsetX;
    } else if (dir.indexOf('w') > -1)
    {
      newRect.width -= offsetX;
      newRect.left += offsetX;
      moveLeft = true;
    }

    if (dir.indexOf('s') > -1)
    {
      newRect.height += offsetY;
    } else if (dir.indexOf('n') > -1)
    {
      newRect.height -= offsetY;
      newRect.top += offsetY;
      moveUp = true;
    }

    const constrain = this._constrainSize(newRect.width, newRect.height, moveLeft, moveUp);
    if (Math.round(newRect.width) !== Math.round(constrain.width))
    { 
      // round to ignore slight round-off errors
      if (dir.indexOf('w') > -1)
      {
        newRect.left += newRect.width - constrain.width;
      }
      newRect.width = constrain.width;
    }

    if (Math.round(newRect.height) !== Math.round(constrain.height))
    {
      if (dir.indexOf('n') > -1)
      {
        newRect.top += newRect.height - constrain.height;
      }
      newRect.height = constrain.height;
    }
    return newRect;
  }

  /** 
   * @internal 
   * Constrain the size to the set min/max values 
   * @param {number} oWidth The original width
   * @param {number} oHeight The original height
   * @param {boolean} moveLeft True if moving left
   * @param {boolean} moveUp True if moving up
   * @returns {Size} The new size
   */
  protected _constrainSize (oWidth: number, oHeight: number, moveLeft: boolean, moveUp: boolean): Size
  {
    const o = this.option;
    const maxWidth = (moveLeft ? o.maxWidthMoveLeft : o.maxWidth) || Number.MAX_SAFE_INTEGER;
    const minWidth = o.minWidth / this.rectScale.x || oWidth;
    const maxHeight = (moveUp ? o.maxHeightMoveUp : o.maxHeight) || Number.MAX_SAFE_INTEGER;
    const minHeight = o.minHeight / this.rectScale.y || oHeight;
    const width = Math.min(maxWidth, Math.max(minWidth, oWidth));
    const height = Math.min(maxHeight, Math.max(minHeight, oHeight));
    return { width, height };
  }

  /** 
   * @internal 
   * Apply the change to the element
   * @returns {DragDropResizable}
   */
  protected _applyChange (): DragDropResizable
  {
    let containmentRect = { left: 0, top: 0, width: 0, height: 0 };
    if (this.el.style.position === 'absolute')
    {
      const containmentEl = this.el.parentElement;
      const { left, top } = containmentEl.getBoundingClientRect();
      containmentRect = { left, top, width: 0, height: 0 };
    }
    if (!this.temporalRect) 
      return this;

    Object.keys(this.temporalRect).forEach(key =>
    {
      const value = this.temporalRect[ key ];
      const scaleReciprocal = key === 'width' || key === 'left' ? this.rectScale.x : key === 'height' || key === 'top' ? this.rectScale.y : 1;
      this.el.style[ key ] = (value - containmentRect[ key ]) * scaleReciprocal + 'px';
    });
    return this;
  }

  /** 
   * @internal 
   * Remove all handlers
   */
  protected _removeHandlers (): DragDropResizable
  {
    this.handlers.forEach(handle => handle.destroy());
    delete this.handlers;
    return this;
  }

  /** 
   * @internal 
   * Returns the current position of the drag element as { left, top }
   * @returns {DragDropUIData}
   */
  protected _ui = (): DragDropUIData =>
  {
    const containmentEl = this.el.parentElement;
    const containmentRect = containmentEl.getBoundingClientRect();
    const newRect = {
      width: this.originalRect.width,
      height: this.originalRect.height + this.scrolled,
      left: this.originalRect.left,
      top: this.originalRect.top - this.scrolled
    };
    const rect = this.temporalRect || newRect;
    return {
      position: {
        left: (rect.left - containmentRect.left) * this.rectScale.x,
        top: (rect.top - containmentRect.top) * this.rectScale.y
      },
      size: {
        width: rect.width * this.rectScale.x,
        height: rect.height * this.rectScale.y
      }
    };
  };
}
